import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button, Input, FormField } from './Form';
import { REGEX, VALIDATION_MESSAGES } from '@pos/shared/constants/validation';

interface TenantRegistrationFormData {
  tenant_name: string;
  slug: string;
  phone?: string;
}

interface TenantRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (tenantId: string) => void;
  token: string;
}

/**
 * 從商家名稱生成 slug 建議
 */
function generateSlugFromName(name: string): string {
  if (!name) return '';

  return (
    name
      .toLowerCase()
      .trim()
      // 將中文字符、空格、標點符號轉換為連字號
      .replace(/[\s\u4e00-\u9fa5\u3000-\u303f\uff00-\uffef]+/g, '-')
      // 將其他非英數字、連字號、底線的字符移除
      .replace(/[^a-z0-9_-]/g, '')
      // 將多個連續的連字號或底線合併為一個連字號
      .replace(/[-_]+/g, '-')
      // 移除開頭和結尾的連字號或底線
      .replace(/^[-_]+|[-_]+$/g, '')
      // 限制長度為 50 個字元
      .slice(0, 50)
  );
}

/**
 * 商家註冊 Modal 組件
 */
export function TenantRegistrationModal({
  isOpen,
  onClose,
  onSuccess,
  token,
}: TenantRegistrationModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const previousTenantNameRef = useRef<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<TenantRegistrationFormData>({
    defaultValues: {
      tenant_name: '',
      slug: '',
      phone: '',
    },
  });

  const tenantName = watch('tenant_name');
  const slug = watch('slug');

  // 當商家名稱改變時，自動生成 slug 建議（僅在用戶未手動編輯 slug 時）
  useEffect(() => {
    if (!isOpen) {
      // Modal 關閉時重置狀態
      setIsSlugManuallyEdited(false);
      previousTenantNameRef.current = '';
      return;
    }

    // 如果用戶手動編輯過 slug，就不再自動更新
    if (isSlugManuallyEdited) {
      return;
    }

    // 如果商家名稱改變了，生成新的 slug 建議
    if (tenantName !== previousTenantNameRef.current) {
      const suggestedSlug = generateSlugFromName(tenantName);

      // 只有在生成的建議與當前 slug 不同時才更新
      // 或者當前 slug 為空時才更新
      if (
        suggestedSlug &&
        (slug === '' ||
          slug === generateSlugFromName(previousTenantNameRef.current))
      ) {
        setValue('slug', suggestedSlug, { shouldValidate: false });
      }

      previousTenantNameRef.current = tenantName;
    }
  }, [tenantName, slug, isSlugManuallyEdited, isOpen, setValue]);

  const handleFormSubmit = async (data: TenantRegistrationFormData) => {
    setIsLoading(true);
    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

      const response = await fetch(`${apiBase}/api/auth/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tenant_name: data.tenant_name,
          slug: data.slug.toLowerCase().trim(),
          phone: data.phone || null,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          toast.error(result.error || '商家識別碼已被使用，請選擇其他識別碼');
          return;
        }
        throw new Error(result.error || '建立商家失敗');
      }

      if (result.success && result.data) {
        toast.success('商家註冊成功！');
        reset();
        // 觸發成功回調（不再傳遞 staff 資訊，因為會重定向到 staff-setup 頁面）
        onSuccess(result.data.tenant_id);
      } else {
        throw new Error(result.error || '建立商家失敗');
      }
    } catch (error) {
      console.error('商家註冊失敗:', error);
      toast.error(
        error instanceof Error ? error.message : '建立商家時發生錯誤'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      reset();
      setIsSlugManuallyEdited(false);
      previousTenantNameRef.current = '';
      onClose();
    }
  };

  // 處理 slug 手動編輯
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 標記為手動編輯
    setIsSlugManuallyEdited(true);
    // 自動轉換為小寫
    e.target.value = e.target.value.toLowerCase();
    register('slug').onChange(e);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>商家註冊</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <FormField
            label="商家名稱"
            required
            error={errors.tenant_name?.message}
          >
            <Input
              {...register('tenant_name', {
                required: '請輸入商家名稱',
                maxLength: {
                  value: 100,
                  message: '商家名稱不能超過100個字元',
                },
              })}
              placeholder="請輸入商家名稱"
              disabled={isLoading}
            />
          </FormField>

          <FormField
            label="商家識別碼"
            required
            error={errors.slug?.message}
            help="只能包含小寫英數字、連字號和底線，3-50個字元"
          >
            <Input
              {...register('slug', {
                required: '請輸入商家識別碼',
                minLength: {
                  value: 3,
                  message: '商家識別碼至少需要3個字元',
                },
                maxLength: {
                  value: 50,
                  message: '商家識別碼不能超過50個字元',
                },
                pattern: {
                  value: /^[a-z0-9_-]+$/,
                  message: '商家識別碼只能包含小寫英數字、連字號和底線',
                },
              })}
              placeholder="例如：my-shop"
              disabled={isLoading}
              onChange={handleSlugChange}
            />
          </FormField>

          <FormField
            label="電話"
            error={errors.phone?.message}
            help="選填，格式：09XXXXXXXX"
          >
            <Input
              type="tel"
              {...register('phone', {
                pattern: {
                  value: REGEX.TAIWAN_PHONE,
                  message: VALIDATION_MESSAGES.TAIWAN_PHONE,
                },
              })}
              placeholder="例如：0912345678"
              maxLength={10}
              disabled={isLoading}
            />
          </FormField>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              取消
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
            >
              確定
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
